/**
 * Boot adapter for a separately served Web shell.
 *
 * The normal `dsh web` path injects the module loader and plugin graph into
 * index.html. A standalone frontend serves its own index instead, so this
 * adapter retrieves the rendered Host index and replays only its boot scripts
 * before {@link AppWebEntry} reads the globals.
 */

const BOOT_URL = '/__dsh/host-index'
const REMOTE_BOOT_MARKER = 'data-dsh-remote-boot'

interface BootWindow {
  __ModuleLoader__?: unknown
  __DSH_BOOT__?: unknown
}

/** Return the Vite-exposed standalone settings without requiring Vite types. */
function envValue(name: string): string | undefined {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env
  if (name === 'VITE_DSH_STANDALONE' && env?.MODE === 'standalone') return 'true'
  return env?.[name]
}

/** Load one classic script from the Host response and preserve execution order. */
function loadClassicScript(source: HTMLScriptElement): Promise<void> {
  const script = document.createElement('script')
  script.setAttribute(REMOTE_BOOT_MARKER, '')
  const src = source.getAttribute('src')
  if (src !== null) {
    script.src = new URL(src, globalThis.location.origin).href
    return new Promise<void>((resolve, reject) => {
      script.addEventListener('load', () => { resolve() }, { once: true })
      script.addEventListener('error', () => { reject(new Error(`standalone boot: failed to load ${script.src}`)) }, { once: true })
      document.head.append(script)
    })
  }
  script.textContent = source.text
  document.head.append(script)
  script.remove()
  return Promise.resolve()
}

/**
 * Replay the loader, preload bundles, and manifest emitted by the Harness Host.
 *
 * Only injection scripts are replayed. The Host's own shell module script is
 * ignored because the standalone page supplies the Vite shell entry.
 */
async function replayHostBoot(html: string): Promise<void> {
  const documentView = new DOMParser().parseFromString(html, 'text/html')
  const scripts = [...documentView.querySelectorAll<HTMLScriptElement>('head script')]
  for (const script of scripts) {
    const src = script.getAttribute('src')
    const isPluginBundle = src !== null && new URL(src, globalThis.location.origin).pathname.startsWith('/plugins/')
    const text = script.text
    const isInlineBoot = src === null && (text.includes('__ModuleLoader__') || text.includes('__DSH_BOOT__'))
    if (isPluginBundle || isInlineBoot) await loadClassicScript(script)
  }
}

/**
 * Ensure the boot globals exist, or retrieve them from the configured Harness.
 * The normal Host-rendered page is a no-op; the standalone Vite mode enables
 * retrieval at build time.
 */
export async function ensureStandaloneHarnessBoot(): Promise<void> {
  const win = globalThis as BootWindow
  if (win.__ModuleLoader__ !== undefined && win.__DSH_BOOT__ !== undefined) return
  if (envValue('VITE_DSH_STANDALONE') !== 'true') return

  const response = await fetch(BOOT_URL, { credentials: 'include' })
  if (!response.ok) throw new Error(`standalone boot: Host index returned HTTP ${response.status}`)
  await replayHostBoot(await response.text())
  if (win.__ModuleLoader__ === undefined || win.__DSH_BOOT__ === undefined) {
    throw new Error('standalone boot: Host index did not provide __ModuleLoader__ and __DSH_BOOT__')
  }
}
